import React, {useState} from "react";
import {Button, DialogActions, DialogContent, DialogTitle, FormControlLabel, Checkbox, Grid, MenuItem, TextField} from "@mui/material";
import {useAsyncAction} from "nate-react-api-helpers";
import {Dialog2} from "../../../../misc/Dialog2";
import {api} from "../../../../api/API";
import {GlazingUpsert} from "../../../../api/Glazing";
import {useProjectId, useProjectUnitSystem} from "../ProjectName";
import {GlassDimensionInput} from "./GlassDimensionInput";
import {formatSmallUnits, normalizeSmallUnits} from "../../../../misc/DimFormat";
import {UnitSystem} from "../../../../api/Projects";
import {useIsQuote} from "../../quote/QuoteContainer";
import {useAlternative} from "../../quote/alternative/Alternative";

// the glass vocabulary, mirroring core/product/tglass
export const glassTypes = [
    {value: "tempered", label: "Tempered"},
    {value: "laminate", label: "Laminate"},
];

export const glassBeads = [
    {value: "snapIn", label: "Snap-in"},
    {value: "screwIn", label: "Screw-in"},
    {value: "thermal", label: "Thermal"},
];

// Creates a one-off glass panel — glass the job needs that no elevation draws. A generated row
// can't be created here: those come from the elevation tool.
export function AddGlazing(props: {
    onDone(): void;
    onClose(): void;
}) {
    const project = useProjectId();
    const isQuote = useIsQuote();
    const alternative = useAlternative();
    const sys = useProjectUnitSystem() || "imperial";

    const [type, setType] = useState("tempered");
    const [thermal, setThermal] = useState(false);
    const [thickness, setThickness] = useState(() => formatSmallUnits(`1/4"`, sys));
    const [bead, setBead] = useState("snapIn");
    const [undersize, setUndersize] = useState(() => formatSmallUnits(`1/8"`, sys));
    const [width, setWidth] = useState("");
    const [height, setHeight] = useState("");
    const [qty, setQty] = useState("1");
    const [note, setNote] = useState("");

    const dimensionsValid = isValidDimension(thickness, sys) &&
        isValidDimension(undersize, sys, true) &&
        isValidDimension(width, sys) &&
        isValidDimension(height, sys);

    const save = useAsyncAction(async () => {
        const upsert: GlazingUpsert = {
            id: 0,
            project: project,
            note: note,
            sortOrder: 0,
            qty: parseInt(qty, 10) || 0,
            archived: false,
            type: type,
            thermal: thermal,
            thickness: normalizeSmallUnits(thickness, sys),
            bead: bead,
            undersize: normalizeSmallUnits(undersize, sys, {allowZero: true}),
            width: normalizeSmallUnits(width, sys),
            height: normalizeSmallUnits(height, sys),
        };

        if(isQuote) {
            await api.quoteGlazing.upsert({...upsert, alternative: alternative});
        } else {
            await api.glazing.upsert(upsert);
        }

        props.onDone();
    }, [project, isQuote, alternative, type, thermal, thickness, bead, undersize, width, height, qty, note, sys]);

    return (
        <Dialog2 open onClose={props.onClose}>
            <DialogTitle>Add Glazing</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{paddingTop: 8}}>
                    <Grid item xs={6}>
                        <TextField select fullWidth size="small" label="Type" value={type}
                                   onChange={e => setType(e.target.value)}>
                            {glassTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField select fullWidth size="small" label="Bead" value={bead}
                                   onChange={e => setBead(e.target.value)}>
                            {glassBeads.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <GlassDimensionInput label="Thickness" value={thickness} onChange={setThickness} />
                    </Grid>
                    <Grid item xs={6}>
                        <GlassDimensionInput label="Undersize" value={undersize} allowZero onChange={setUndersize} />
                    </Grid>
                    <Grid item xs={6}>
                        <GlassDimensionInput label="Glass Width" value={width} onChange={setWidth} />
                    </Grid>
                    <Grid item xs={6}>
                        <GlassDimensionInput label="Glass Height" value={height} onChange={setHeight} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Qty" value={qty}
                                   onChange={e => setQty(e.target.value)} />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={thermal} onChange={e => setThermal(e.target.checked)} />}
                            label="Thermal" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Note" value={note}
                                   onChange={e => setNote(e.target.value)} />
                    </Grid>
                </Grid>
                {save.LoadingElement}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Cancel</Button>
                <Button variant="contained" disabled={save.loading || !dimensionsValid}
                        onClick={() => save.callback(null)}>
                    Add
                </Button>
            </DialogActions>
        </Dialog2>
    )
}

function isValidDimension(value: string, sys: UnitSystem, allowZero?: boolean) {
    try {
        normalizeSmallUnits(value, sys, {allowZero: allowZero});
        return true;
    } catch (e) {
        return false;
    }
}
